import { nextTick } from "vue";
import { createI18n } from "vue-i18n";

// Import language files
import en from "@/locales/en.json";
import de from "@/locales/de.json";
import nl from "@/locales/nl.json";

// Constants
export const SUPPORT_LOCALES = ["en", "de", "nl"];
const DEFAULT_LOCALE = "en";
const MESSAGES = { en, de, nl };

/**
 * Detects user's browser language and matches it with supported locales
 * @returns {string|null} Matched language code or null
 */
function detectBrowserLanguage() {
  const browserLang = navigator.language;
  const browserLangBase = browserLang.split("-")[0];
  
  // Try exact match first
  const exactMatch = SUPPORT_LOCALES.find(lang => lang === browserLang);
  if (exactMatch) return exactMatch;
  
  // Try base language match
  return SUPPORT_LOCALES.find(lang => lang === browserLangBase) || null;
}

/**
 * Creates and configures i18n instance
 * @param {Object} options - Configuration options
 * @returns {i18n} Configured i18n instance
 */
export function setupI18n(options = { locale: DEFAULT_LOCALE }) {
  const i18n = createI18n(options);
  setI18nLanguage(i18n, options.locale);
  return i18n;
}

/**
 * Sets the language for i18n instance and HTML document
 * @param {i18n} i18n - I18n instance
 * @param {string} locale - Locale to set
 */
export function setI18nLanguage(i18n, locale) {
  const localeValue = i18n.mode === "legacy" 
    ? (i18n.global.locale = locale)
    : (i18n.global.locale.value = locale);
    
  document.querySelector("html").setAttribute("lang", locale);
  return localeValue;
}

/**
 * Dynamically loads locale messages
 * @param {i18n} i18n - I18n instance
 * @param {string} locale - Locale to load
 * @returns {Promise} Next tick promise
 */
export async function loadLocaleMessages(i18n, locale) {
  if (!locale) return nextTick();

  try {
    const messages = await import(
      /* webpackChunkName: "locale-[request]" */ `./locales/${locale}.json`
    );
    i18n.global.setLocaleMessage(locale, messages.default);
    return nextTick();
  } catch (error) {
    console.error(`Failed to load locale messages for ${locale}:`, error);
    return nextTick();
  }
}

// Create i18n instance with default configuration
const i18n = setupI18n({
  globalInjection: true,
  legacy: false,
  allowComposition: true,
  locale: detectBrowserLanguage() || import.meta.env.VITE_I18N_LOCALE || DEFAULT_LOCALE,
  fallbackLocale: import.meta.env.VITE_I18N_FALLBACK_LOCALE || DEFAULT_LOCALE,
  messages: MESSAGES,
});

export default i18n;